type Base64UrlString = string;

/**
 * OAuth `state` length needs to stay small. Instead of embedding a full RSA JWK,
 * we embed an ECDH P-256 public key in RAW (uncompressed) form (65 bytes).
 *
 * state = `${prefix}${base64url(rawPublicKeyBytes)}`
 */
export const OAUTH_STATE_PUBKEY_PREFIX = 'halo_ecdh_v1.';

export interface OAuthStateEncryptedPayloadV1 {
  v: 1;
  alg: 'ECDH-P256+A256GCM';
  spk: Base64UrlString; // sender public key (raw) for ECDH
  iv: Base64UrlString; // AES-GCM iv (12 bytes)
  ct: Base64UrlString; // AES-GCM ciphertext (includes tag)
}

function assertWebCryptoAvailable(): void {
  if (!globalThis.crypto?.subtle) {
    throw new Error('WebCrypto is not available in this environment.');
  }
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  // btoa is available in browsers
  return btoa(binary);
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function base64ToBase64Url(base64: string): string {
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64UrlToBase64(b64url: string): string {
  const base64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = (4 - (base64.length % 4)) % 4;
  return base64 + '='.repeat(pad);
}

function bytesToBase64Url(bytes: Uint8Array): string {
  return base64ToBase64Url(bytesToBase64(bytes));
}

function base64UrlToBytes(b64url: string): Uint8Array {
  return base64ToBytes(base64UrlToBase64(b64url));
}

function utf8ToBytes(str: string): Uint8Array {
  return new TextEncoder().encode(str);
}

function bytesToUtf8(bytes: Uint8Array): string {
  return new TextDecoder().decode(bytes);
}

// --- P-256 compressed public key helpers ---
// P-256 (secp256r1) prime field and curve parameters
const P256_P = BigInt('0xffffffff00000001000000000000000000000000ffffffffffffffffffffffff');
const P256_A = BigInt(-3); // a = -3
const P256_B = BigInt('0x5ac635d8aa3a93e7b3ebbd55769886bc651d06b0cc53b0f63bce3c3e27d2604b');

function mod(a: bigint, m: bigint): bigint {
  const res = a % m;
  return res >= BigInt(0) ? res : res + m;
}

function modPow(base: bigint, exp: bigint, m: bigint): bigint {
  let result = BigInt(1);
  let b = mod(base, m);
  let e = exp;
  while (e > BigInt(0)) {
    if (e & BigInt(1)) result = mod(result * b, m);
    b = mod(b * b, m);
    e >>= BigInt(1);
  }
  return result;
}

function isOdd(n: bigint): boolean {
  return (n & BigInt(1)) === BigInt(1);
}

function bytesToBigInt(bytes: Uint8Array): bigint {
  let hex = '';
  for (const b of bytes) hex += b.toString(16).padStart(2, '0');
  return BigInt(`0x${hex || '0'}`);
}

function bigIntToFixedBytes(n: bigint, length: number): Uint8Array {
  let hex = n.toString(16);
  if (hex.length % 2) hex = `0${hex}`;
  const bytes = new Uint8Array(length);
  const raw = hex.match(/.{1,2}/g)?.map((x) => parseInt(x, 16)) ?? [];
  const start = length - raw.length;
  for (let i = 0; i < raw.length; i++) bytes[start + i] = raw[i]!;
  return bytes;
}

/**
 * Uncompressed P-256 raw public key (65 bytes): 0x04 || X(32) || Y(32)
 * Compressed format (33 bytes): 0x02/0x03 || X(32)  (parity of Y)
 */
export function compressP256PublicKeyRaw(uncompressed: Uint8Array): Uint8Array {
  if (uncompressed.length !== 65 || uncompressed[0] !== 0x04) {
    throw new Error('Invalid uncompressed P-256 public key.');
  }
  const x = uncompressed.slice(1, 33);
  const y = bytesToBigInt(uncompressed.slice(33, 65));
  const prefix = isOdd(y) ? 0x03 : 0x02;
  const out = new Uint8Array(33);
  out[0] = prefix;
  out.set(x, 1);
  return out;
}

/**
 * Decompress compressed P-256 public key into uncompressed raw (65 bytes).
 * Uses y^2 = x^3 + ax + b (mod p) and chooses y parity based on prefix.
 */
export function decompressP256PublicKeyRaw(compressed: Uint8Array): Uint8Array {
  if (compressed.length !== 33 || (compressed[0] !== 0x02 && compressed[0] !== 0x03)) {
    throw new Error('Invalid compressed P-256 public key.');
  }
  const prefix = compressed[0];
  const x = bytesToBigInt(compressed.slice(1, 33));

  // rhs = x^3 + a*x + b mod p
  const x2 = mod(x * x, P256_P);
  const x3 = mod(x2 * x, P256_P);
  const rhs = mod(x3 + mod(P256_A * x, P256_P) + P256_B, P256_P);

  // For P-256, p % 4 == 3, so sqrt can be computed as rhs^((p+1)/4)
  const exp = (P256_P + BigInt(1)) / BigInt(4);
  let y = modPow(rhs, exp, P256_P);

  const wantOdd = prefix === 0x03;
  if (isOdd(y) !== wantOdd) {
    y = mod(P256_P - y, P256_P);
  }

  const out = new Uint8Array(65);
  out[0] = 0x04;
  out.set(bigIntToFixedBytes(x, 32), 1);
  out.set(bigIntToFixedBytes(y, 32), 33);
  return out;
}

async function sha256Base64Url(input: string): Promise<string> {
  assertWebCryptoAvailable();
  const digest = await crypto.subtle.digest('SHA-256', utf8ToBytes(input));
  return bytesToBase64Url(new Uint8Array(digest));
}

function isProbablyJsonObjectString(input: string): boolean {
  const s = input.trim();
  return s.startsWith('{') && s.endsWith('}');
}

export function isOAuthStateWithPublicKey(state: string | null | undefined): state is string {
  return typeof state === 'string' && state.startsWith(OAUTH_STATE_PUBKEY_PREFIX);
}

export function encodeOAuthStateFromPublicKeyRaw(publicKeyRaw: Uint8Array): string {
  return `${OAUTH_STATE_PUBKEY_PREFIX}${bytesToBase64Url(publicKeyRaw)}`;
}

export function decodeOAuthStatePublicKeyRaw(state: string): Uint8Array {
  if (!isOAuthStateWithPublicKey(state)) {
    throw new Error('Invalid OAuth state: missing public key prefix.');
  }
  const encoded = state.slice(OAUTH_STATE_PUBKEY_PREFIX.length);
  return base64UrlToBytes(encoded);
}

async function importEcdhPublicKeyRaw(publicKeyRaw: Uint8Array): Promise<CryptoKey> {
  assertWebCryptoAvailable();
  // WebCrypto expects uncompressed (65 bytes) raw for P-256.
  const uncompressed = decompressP256PublicKeyRaw(publicKeyRaw);
  return crypto.subtle.importKey(
    'raw',
    uncompressed,
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    []
  );
}

async function importEcdhPrivateKeyJwk(privateJwk: JsonWebKey): Promise<CryptoKey> {
  assertWebCryptoAvailable();
  return crypto.subtle.importKey(
    'jwk',
    privateJwk,
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    ['deriveBits']
  );
}

async function importAesKeyRaw(keyBytes: Uint8Array, usage: KeyUsage[]): Promise<CryptoKey> {
  assertWebCryptoAvailable();
  return crypto.subtle.importKey('raw', keyBytes, { name: 'AES-GCM' }, false, usage);
}

export async function createOAuthStateKeyPair(): Promise<{
  state: string;
  privateJwk: JsonWebKey;
}> {
  assertWebCryptoAvailable();
  const keyPair = (await crypto.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, [
    'deriveBits',
  ])) as CryptoKeyPair;

  const publicRaw = new Uint8Array(await crypto.subtle.exportKey('raw', keyPair.publicKey));
  const publicCompressed = compressP256PublicKeyRaw(publicRaw);
  const privateJwk = (await crypto.subtle.exportKey('jwk', keyPair.privateKey)) as JsonWebKey;

  const state = encodeOAuthStateFromPublicKeyRaw(publicCompressed);
  return { state, privateJwk };
}

/**
 * Store private key in sessionStorage using a stable key derived from OAuth state.
 * This allows cross-origin navigation and full reloads; the key remains available when coming back.
 */
export async function saveOAuthStatePrivateKeyToSessionStorage(
  state: string,
  privateJwk: JsonWebKey
): Promise<void> {
  const storageKey = await sha256Base64Url(state);
  const key = `halo.oauth.priv.${storageKey}`;
  const metaKey = `halo.oauth.privmeta.${storageKey}`;
  sessionStorage.setItem(key, JSON.stringify(privateJwk));
  sessionStorage.setItem(metaKey, JSON.stringify({ createdAt: Date.now() }));
}

export async function loadOAuthStatePrivateKeyFromSessionStorage(
  state: string
): Promise<JsonWebKey | null> {
  const storageKey = await sha256Base64Url(state);
  const key = `halo.oauth.priv.${storageKey}`;
  const raw = sessionStorage.getItem(key);
  if (!raw) return null;
  return JSON.parse(raw) as JsonWebKey;
}

export async function deleteOAuthStatePrivateKeyFromSessionStorage(state: string): Promise<void> {
  const storageKey = await sha256Base64Url(state);
  sessionStorage.removeItem(`halo.oauth.priv.${storageKey}`);
  sessionStorage.removeItem(`halo.oauth.privmeta.${storageKey}`);
}

export async function encryptForOAuthState(state: string, plaintext: string): Promise<string> {
  // Backward compatibility: if state is not our public-key format, keep plaintext.
  if (!isOAuthStateWithPublicKey(state)) {
    return plaintext;
  }

  assertWebCryptoAvailable();

  const recipientPublicRaw = decodeOAuthStatePublicKeyRaw(state);
  const recipientPublicKey = await importEcdhPublicKeyRaw(recipientPublicRaw);

  // Sender generates an ephemeral ECDH keypair per encryption.
  const senderKeyPair = (await crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    ['deriveBits']
  )) as CryptoKeyPair;

  const sharedBits = await crypto.subtle.deriveBits(
    { name: 'ECDH', public: recipientPublicKey },
    senderKeyPair.privateKey,
    256
  );

  const aesKey = await importAesKeyRaw(new Uint8Array(sharedBits), ['encrypt']);
  const iv = crypto.getRandomValues(new Uint8Array(12));

  const ctBuf = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    aesKey,
    utf8ToBytes(plaintext)
  );

  const senderPublicRaw = new Uint8Array(
    await crypto.subtle.exportKey('raw', senderKeyPair.publicKey)
  );
  const senderPublicCompressed = compressP256PublicKeyRaw(senderPublicRaw);

  const payload: OAuthStateEncryptedPayloadV1 = {
    v: 1,
    alg: 'ECDH-P256+A256GCM',
    spk: bytesToBase64Url(senderPublicCompressed),
    iv: bytesToBase64Url(iv),
    ct: bytesToBase64Url(new Uint8Array(ctBuf)),
  };

  return JSON.stringify(payload);
}

export async function decryptForOAuthState(
  state: string,
  encryptedOrPlain: string
): Promise<string> {
  // If it's not our state format, treat it as plaintext (legacy behavior)
  if (!isOAuthStateWithPublicKey(state)) {
    return encryptedOrPlain;
  }

  // If it doesn't look like our encrypted JSON payload, treat as plaintext (legacy behavior)
  if (!isProbablyJsonObjectString(encryptedOrPlain)) {
    return encryptedOrPlain;
  }

  let payload: OAuthStateEncryptedPayloadV1 | null = null;
  try {
    payload = JSON.parse(encryptedOrPlain) as OAuthStateEncryptedPayloadV1;
  } catch {
    return encryptedOrPlain;
  }

  if (!payload || payload.v !== 1 || payload.alg !== 'ECDH-P256+A256GCM') {
    return encryptedOrPlain;
  }

  const privateJwk = await loadOAuthStatePrivateKeyFromSessionStorage(state);
  if (!privateJwk) {
    throw new Error('Missing private key for OAuth state. Please restart the login flow.');
  }

  assertWebCryptoAvailable();

  const privateKey = await importEcdhPrivateKeyJwk(privateJwk);
  const senderPublicKey = await importEcdhPublicKeyRaw(base64UrlToBytes(payload.spk));
  const iv = base64UrlToBytes(payload.iv);
  const ct = base64UrlToBytes(payload.ct);

  const sharedBits = await crypto.subtle.deriveBits(
    { name: 'ECDH', public: senderPublicKey },
    privateKey,
    256
  );

  const aesKey = await importAesKeyRaw(new Uint8Array(sharedBits), ['decrypt']);
  const ptBuf = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, aesKey, ct);
  return bytesToUtf8(new Uint8Array(ptBuf));
}
